import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { TestResult } from '../types/testResults'

interface UseTestResultsReturn {
  testResults: TestResult[]
  loading: boolean
  error: string | null
  addTestResult: (testResult: Omit<TestResult, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateTestResult: (id: string, testResult: Omit<TestResult, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  deleteTestResult: (id: string) => Promise<void>
  refreshTestResults: () => Promise<void>
}

export const useTestResults = (athleteId: string): UseTestResultsReturn => {
  const [testResults, setTestResults] = useState<TestResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadTestResults = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: supabaseError } = await supabase
        .from('test_results')
        .select('*')
        .eq('user_id', athleteId)
        .order('date', { ascending: false })

      if (supabaseError) {
        throw new Error(supabaseError.message)
      }

      // Transform Supabase data to our TestResult type
      const transformedData: TestResult[] = (data || []).map(
        (row) =>
          ({
            id: row.id,
            userId: row.user_id,
            date: row.date,
            discipline: row.discipline,
            testType: row.test_type,
            description: row.description,
            time: row.time,
            time400m: row.time_400m,
            time200m: row.time_200m,
            avgPower: row.avg_power,
            maxPower: row.max_power,
            avgHeartRate: row.avg_heart_rate,
            maxHeartRate: row.max_heart_rate,
            css: row.css,
            ftp: row.ftp,
            weather: row.weather,
            notes: row.notes,
            status: row.status,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
          }) as TestResult
      )

      setTestResults(transformedData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load test results')
      console.error('Error loading test results:', err)
    } finally {
      setLoading(false)
    }
  }, [athleteId])

  const addTestResult = useCallback(
    async (testResultData: Omit<TestResult, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        setError(null)

        // Transform our TestResult type to Supabase format
        const supabaseData = {
          user_id: testResultData.userId,
          description: testResultData.description,
          date: testResultData.date,
          discipline: testResultData.discipline,
          test_type: testResultData.testType,
          time: testResultData.time,
          time_400m: testResultData.time400m,
          time_200m: testResultData.time200m,
          avg_power: testResultData.avgPower,
          max_power: testResultData.maxPower,
          avg_heart_rate: testResultData.avgHeartRate,
          max_heart_rate: testResultData.maxHeartRate,
          css: testResultData.css,
          ftp: testResultData.ftp,
          weather: testResultData.weather,
          notes: testResultData.notes,
          status: testResultData.status,
        }

        const { error: supabaseError } = await supabase.from('test_results').insert(supabaseData)

        if (supabaseError) {
          throw new Error(supabaseError.message)
        }

        // Refresh the data after successful insert
        await loadTestResults()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add test result'
        setError(errorMessage)
        console.error('Error adding test result:', err)
        throw new Error(errorMessage)
      }
    },
    [loadTestResults]
  )

  const updateTestResult = useCallback(
    async (id: string, testResultData: Omit<TestResult, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        setError(null)

        // Transform our TestResult type to Supabase format
        const supabaseData = {
          user_id: testResultData.userId,
          description: testResultData.description,
          date: testResultData.date,
          discipline: testResultData.discipline,
          test_type: testResultData.testType,
          time: testResultData.time,
          time_400m: testResultData.time400m,
          time_200m: testResultData.time200m,
          avg_power: testResultData.avgPower,
          max_power: testResultData.maxPower,
          avg_heart_rate: testResultData.avgHeartRate,
          max_heart_rate: testResultData.maxHeartRate,
          css: testResultData.css,
          ftp: testResultData.ftp,
          weather: testResultData.weather,
          notes: testResultData.notes,
          status: testResultData.status,
          updated_at: new Date().toISOString(),
        }

        const { error: supabaseError } = await supabase.from('test_results').update(supabaseData).eq('id', id)

        if (supabaseError) {
          throw new Error(supabaseError.message)
        }

        // Refresh the data after successful update
        await loadTestResults()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update test result'
        setError(errorMessage)
        console.error('Error updating test result:', err)
        throw new Error(errorMessage)
      }
    },
    [loadTestResults]
  )

  const deleteTestResult = useCallback(
    async (id: string) => {
      try {
        setError(null)

        const { error: supabaseError } = await supabase.from('test_results').delete().eq('id', id)

        if (supabaseError) {
          throw new Error(supabaseError.message)
        }

        // Refresh the data after successful delete
        await loadTestResults()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete test result'
        setError(errorMessage)
        console.error('Error deleting test result:', err)
        throw new Error(errorMessage)
      }
    },
    [loadTestResults]
  )

  const refreshTestResults = useCallback(async () => {
    await loadTestResults()
  }, [loadTestResults])

  // Load test results on mount and when athleteId changes
  useEffect(() => {
    if (athleteId) {
      loadTestResults()
    }
  }, [athleteId, loadTestResults])

  return {
    testResults,
    loading,
    error,
    addTestResult,
    updateTestResult,
    deleteTestResult,
    refreshTestResults,
  }
}
